import * as yup from 'yup';

import { ManufacturerPayload } from 'api/manufacturer';
import { Manufacturer } from 'types/manufacturer';

export const ADDRESS_FIELDS = [
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'zip',
  'country',
] as const;

type AddressField = typeof ADDRESS_FIELDS[number];

export type ManufacturerFormValues = {
  name: string;
  /** One input per email; blank rows are dropped on save. */
  email: string[];
  quickbooksVendorId: string;
} & Record<AddressField, string>;

/**
 * A manufacturer's emails as a list. The API now always sends a list, but
 * older documents (and an API not yet updated) hold a single string.
 */
export const emailList = (email: unknown): string[] =>
  (Array.isArray(email) ? email : typeof email === 'string' ? [email] : [])
    .map((e) => (typeof e === 'string' ? e.trim() : ''))
    .filter(Boolean);

/** The form's starting values: the manufacturer's, or blank for a new one. */
export const toFormValues = (
  manufacturer?: Manufacturer | null,
): ManufacturerFormValues => {
  const emails = emailList(manufacturer?.email);
  const address = manufacturer?.address ?? {};

  return {
    name: manufacturer?.name ?? '',
    // Always one row, so there's an input to type the first email into.
    email: emails.length ? emails : [''],
    quickbooksVendorId: manufacturer?.quickbooksVendorId ?? '',
    addressLine1: address.addressLine1 ?? '',
    addressLine2: address.addressLine2 ?? '',
    city: address.city ?? '',
    state: address.state ?? '',
    zip: address.zip ?? '',
    country: address.country ?? '',
  };
};

const hasAddress = (values: Partial<ManufacturerFormValues>) =>
  ADDRESS_FIELDS.some((field) => (values[field] ?? '').trim() !== '');

/**
 * The API body: trimmed, blank emails dropped, and the address left out when
 * none of it was filled in (a manufacturer may have no address yet).
 */
export const toPayload = (
  values: ManufacturerFormValues,
): ManufacturerPayload => {
  const trimmed = (field: AddressField) => values[field].trim();

  return {
    name: values.name.trim(),
    email: values.email.map((e) => e.trim()).filter(Boolean),
    quickbooksVendorId: values.quickbooksVendorId.trim(),
    ...(hasAddress(values)
      ? {
          address: {
            addressLine1: trimmed('addressLine1'),
            addressLine2: trimmed('addressLine2'),
            city: trimmed('city'),
            state: trimmed('state'),
            zip: trimmed('zip'),
            country: trimmed('country'),
          },
        }
      : {}),
  };
};

/** Required only once any part of the address is filled in, as the API does. */
const requiredWithAddress = (label: string) =>
  yup
    .string()
    .max(128)
    .test('required-with-address', `${label} is required`, function (value) {
      return !hasAddress(this.parent) || (value ?? '').trim() !== '';
    });

export const validationSchema = yup.object({
  name: yup.string().trim().required('Name is required').max(128),
  email: yup
    .array()
    .of(yup.string().trim().email('Enter a valid email'))
    .max(20),
  quickbooksVendorId: yup.string().max(64),
  addressLine1: requiredWithAddress('Address line 1'),
  addressLine2: yup.string().max(128),
  city: requiredWithAddress('City'),
  state: requiredWithAddress('State'),
  zip: requiredWithAddress('Zip'),
  country: yup.string().max(128),
});
